/**
 * ============================================================================
 * WebhookAdmin — Phase 3 frontend
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: tenant-admin screen to manage outbound webhook subscriptions.
 *          List + create (HTTPS-only validated client-side AND server-side) +
 *          delete + rotate-secret. The secret bytes are NEVER displayed —
 *          only secret_version.
 *
 * UX RULES:
 *   - target_url field validates HTTPS prefix client-side; server re-validates.
 *   - "Rotate secret" button shows a confirmation toast (rotation is
 *     irreversible from the UI; old secrets remain RETIRED in the registry).
 *   - Recent deliveries panel shows last 10 with state + retry_count.
 *
 * URS rows: WH-001..010, SEC-027.
 * ============================================================================
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import {
  useWebhooks,
  useCreateWebhook,
  useDeleteWebhook,
  useRotateWebhookSecret,
} from '../../api/hooks';
import styles from '../AuthorWorkbench/NewDocumentForm.module.css';
import wstyles from './WebhookAdmin.module.css';

export function WebhookAdmin() {
  const { t } = useTranslation();
  const subsQ = useWebhooks();
  const createMut = useCreateWebhook();
  const deleteMut = useDeleteWebhook();
  const rotateMut = useRotateWebhookSecret();

  const [createOpen, setCreateOpen] = useState(false);
  const [targetUrl, setTargetUrl] = useState('');
  const [eventTypesInput, setEventTypesInput] = useState('workflow.transition,signature.applied');
  const [formError, setFormError] = useState<string | null>(null);

  function onCreate(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!targetUrl.startsWith('https://')) {
      setFormError(t('webhookAdmin.errors.httpsRequired', {
        defaultValue: 'target_url must start with https://',
      }));
      return;
    }
    const eventTypes = eventTypesInput.split(',').map((s) => s.trim()).filter(Boolean);
    if (eventTypes.length === 0) {
      setFormError(t('webhookAdmin.errors.eventTypesRequired', {
        defaultValue: 'At least one event_type is required.',
      }));
      return;
    }
    createMut.mutate(
      { target_url: targetUrl, event_types: eventTypes },
      {
        onSuccess: () => {
          setTargetUrl('');
          setEventTypesInput('workflow.transition,signature.applied');
          setCreateOpen(false);
          subsQ.refetch();
        },
        onError: (err) =>
          setFormError(err instanceof Error ? err.message : 'Failed to create subscription'),
      },
    );
  }

  function onRotate(id: string) {
    if (!confirm(t('webhookAdmin.confirm.rotate', {
      defaultValue: 'Rotate the secret? Old signatures remain valid for historical verification.',
    }))) return;
    rotateMut.mutate({ webhookId: id }, { onSuccess: () => subsQ.refetch() });
  }

  function onDelete(id: string) {
    if (!confirm(t('webhookAdmin.confirm.delete', {
      defaultValue: 'Delete this subscription? Future events will not be delivered to this target.',
    }))) return;
    deleteMut.mutate({ webhookId: id }, { onSuccess: () => subsQ.refetch() });
  }

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('webhookAdmin.eyebrow', { defaultValue: 'Admin' })}
        title={t('webhookAdmin.title', { defaultValue: 'Webhook subscriptions' })}
        subtitle={t('webhookAdmin.subtitle', {
          defaultValue:
            'HTTPS-only endpoints that receive event notifications signed with HMAC-SHA-256.',
        })}
        actions={
          <Button variant="primary" leadingIcon="+" onClick={() => setCreateOpen(true)}>
            {t('webhookAdmin.actions.new', { defaultValue: 'New webhook' })}
          </Button>
        }
      />

      {createOpen && (
        <div className={styles.backdrop} role="presentation" onClick={(e) => {
          if (e.target === e.currentTarget) setCreateOpen(false);
        }}>
          <div
            className={styles.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="wh-create-title"
          >
            <header className={styles.header}>
              <h2 id="wh-create-title" className={styles.title}>
                {t('webhookAdmin.create.title', { defaultValue: 'Create webhook' })}
              </h2>
              <p className={styles.desc}>
                {t('webhookAdmin.create.subtitle', {
                  defaultValue: 'A new HMAC-SHA-256 secret will be generated. You will not see it again.',
                })}
              </p>
            </header>
            <form className={styles.form} onSubmit={onCreate} noValidate>
              {formError && (
                <div role="alert" className={styles.errorBanner}>{formError}</div>
              )}
              <div className={styles.field}>
                <label htmlFor="wh-target" className={styles.label}>
                  {t('webhookAdmin.fields.targetUrl', { defaultValue: 'Target URL (HTTPS)' })}
                  <span className={styles.required} aria-hidden="true">*</span>
                </label>
                <input
                  id="wh-target"
                  type="url"
                  className={styles.input}
                  value={targetUrl}
                  onChange={(e) => setTargetUrl(e.target.value)}
                  placeholder="https://hooks.example.com/edms"
                  pattern="https://.+"
                  required
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="wh-events" className={styles.label}>
                  {t('webhookAdmin.fields.eventTypes', { defaultValue: 'Event types (comma-separated)' })}
                </label>
                <input
                  id="wh-events"
                  type="text"
                  className={styles.input}
                  value={eventTypesInput}
                  onChange={(e) => setEventTypesInput(e.target.value)}
                />
              </div>
              <footer className={styles.footer}>
                <Button type="button" variant="secondary" onClick={() => setCreateOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" variant="primary" isLoading={createMut.isPending}>
                  {t('webhookAdmin.create.submit', { defaultValue: 'Create subscription' })}
                </Button>
              </footer>
            </form>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <h2 className={wstyles.sectionTitle}>
            {t('webhookAdmin.subscriptions.title', { defaultValue: 'Subscriptions' })}
          </h2>
        </CardHeader>
        <CardBody>
          {subsQ.isLoading && <p role="status">{t('common.loading', { defaultValue: 'Loading…' })}</p>}
          {subsQ.isError && (
            <p role="alert" className={wstyles.error}>
              {t('webhookAdmin.errors.loadFailed', { defaultValue: 'Could not load subscriptions.' })}
            </p>
          )}
          {(subsQ.data?.items ?? []).length === 0 && !subsQ.isLoading && (
            <p className={wstyles.empty}>
              {t('webhookAdmin.empty', { defaultValue: 'No subscriptions yet.' })}
            </p>
          )}
          <table className={wstyles.table}>
            <thead>
              <tr>
                <th scope="col">{t('webhookAdmin.columns.targetUrl', { defaultValue: 'Target URL' })}</th>
                <th scope="col">{t('webhookAdmin.columns.eventTypes', { defaultValue: 'Event types' })}</th>
                <th scope="col">{t('webhookAdmin.columns.secretVersion', { defaultValue: 'Secret v' })}</th>
                <th scope="col">{t('webhookAdmin.columns.actions', { defaultValue: 'Actions' })}</th>
              </tr>
            </thead>
            <tbody>
              {(subsQ.data?.items ?? []).map((sub) => (
                <tr key={sub.webhook_id}>
                  <td><code>{sub.target_url}</code></td>
                  <td>{sub.event_types?.join(', ') ?? ''}</td>
                  <td>v{sub.secret_version}</td>
                  <td>
                    <div className={wstyles.rowActions}>
                      <Button size="sm" variant="tertiary" onClick={() => onRotate(sub.webhook_id)}>
                        {t('webhookAdmin.actions.rotate', { defaultValue: 'Rotate' })}
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => onDelete(sub.webhook_id)}>
                        {t('common.delete', { defaultValue: 'Delete' })}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}
